import { FunctionComponent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { faCheck, faCircleInfo, faLanguage } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { LoadingBlock } from "@/components/Spinner/LoadingBlock";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";

import { SenderLanguageFields } from "@/components/SenderLanguage/SenderLanguageFields";
import {
	hasSenderLanguageChanges,
	SenderLanguageValue,
	senderLanguageUpsertOf,
	senderLanguageValueOf,
} from "@/components/SenderLanguage/senderLanguage.utils";

import {
	useGetSenderCopywritingPreferenceQuery,
	useUpsertSenderCopywritingPreferenceMutation,
} from "@/pages/Private/redux/senderCopywriting/senderCopywriting.api";

import { SettingsCard } from "./SettingsCard";

/**
 * "Copywriting settings" (PREC-3686): the languages the AI copywriting agent
 * may write in, plus the sender's typical phrasings. The same preference the
 * onboarding tour's LANGUAGE step captures — until now that step was the only
 * place it could ever be set, so it was write-once.
 */
export const CopywritingSettingsCard: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`account.copywriting.${key}`);

	const { data: preference, isLoading: isPreferenceLoading } =
		useGetSenderCopywritingPreferenceQuery();
	const [upsert, { isLoading }] = useUpsertSenderCopywritingPreferenceMutation();

	const [value, setValue] = useState<SenderLanguageValue>(senderLanguageValueOf(preference));
	const [showSavedFlash, setShowSavedFlash] = useState(false);
	const savedFlashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const mountedRef = useRef(true);

	useEffect(() => {
		setValue(senderLanguageValueOf(preference));
	}, [preference]);

	// A flash timer that outlives the card calls setShowSavedFlash on a gone
	// tree; clear it on unmount (PREC-5115). `mountedRef` also stops a save
	// that settles after the unmount from arming a fresh one.
	useEffect(() => {
		mountedRef.current = true;

		return () => {
			mountedRef.current = false;

			if (savedFlashTimerRef.current) {
				clearTimeout(savedFlashTimerRef.current);
			}
		};
	}, []);

	const isDirty = hasSenderLanguageChanges(value, preference);

	const onSave = async () => {
		try {
			await upsert(senderLanguageUpsertOf(value)).unwrap();

			if (!mountedRef.current) {
				return;
			}

			if (savedFlashTimerRef.current) {
				clearTimeout(savedFlashTimerRef.current);
			}

			setShowSavedFlash(true);
			savedFlashTimerRef.current = setTimeout(() => setShowSavedFlash(false), 1600);
		} catch (err) {
			console.error(err);
		}
	};

	const footer = (
		<>
			{showSavedFlash && (
				<span className="mr-1 flex items-center gap-1 text-[12.5px] font-semibold text-status-success">
					<Icon className="h-3.5 w-3.5" icon={faCheck} />
					{t("account.saved")}
				</span>
			)}
			<Button
				color={ButtonColor.ACTION_SECONDARY}
				disabled={!isDirty}
				size={ButtonSize.S}
				title={t("basics.cancel")}
				type="button"
				onClick={() => setValue(senderLanguageValueOf(preference))}
			/>
			<Button
				color={ButtonColor.ACTION}
				disabled={!isDirty}
				isLoading={isLoading}
				size={ButtonSize.S}
				title={t("basics.saveChanges")}
				type="button"
				onClick={onSave}
			/>
		</>
	);

	return (
		<SettingsCard footer={footer} icon={faLanguage} subtitle={ts("subtitle")} title={ts("title")}>
			<div className="flex flex-col gap-5 px-5 py-4">
				{/*
				 * Only editable once the stored preference is in: the effect above re-syncs
				 * `value` whenever it arrives, so a form rendered during the request would
				 * silently discard whatever the user picked in the meantime.
				 */}
				{isPreferenceLoading ? (
					<LoadingBlock />
				) : (
					<SenderLanguageFields value={value} onChange={setValue} />
				)}

				<div className="flex max-w-[660px] items-start gap-2.5 rounded-lg bg-blue-action-200 px-3.5 py-3 text-[12.5px] leading-relaxed text-text-meta">
					<Icon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-brand-light" icon={faCircleInfo} />
					<span>{ts("resolutionHint")}</span>
				</div>
			</div>
		</SettingsCard>
	);
};
